import { getAbsoluteImageUrl } from '../../../utils/imageUrl';

/** The photo kinds the Documentos tab shows one fixed panel for. */
export type DocumentSlotType = 'document_front' | 'document_back' | 'vehicle';

/** Every photo kind the tab can start a capture for — the panels, plus the open-ended list. */
export type DocumentPhotoType = DocumentSlotType | 'other';

/** Everything the tab needs about one panel, plus what its update would replace. */
export interface DocumentSlot {
  type: DocumentSlotType;
  /** Heading above the panel. */
  title: string;
  /** What the photo is called once it leaves the tab — lightbox caption, capture modal title. */
  caption: string;
  /** The button an empty panel offers. */
  addLabel: string;
  /** Absolutized URL of the photo in place, or '' when the slot is empty. */
  url: string;
  /**
   * The `identity_photos` row behind `url`, when there is one.
   *
   * Undefined means the photo came from the legacy `people.document_*_url` column, which has
   * no row to delete — the update still works, it just adds rather than replaces.
   */
  photoId?: string;
}

interface SlotPhotoRecord {
  id: string;
  photo_url: string;
  photo_type?: string;
}

interface SlotPerson {
  identity_photos?: SlotPhotoRecord[];
  document_front_url?: string;
  document_back_url?: string;
}

const PANELS: Array<Omit<DocumentSlot, 'url' | 'photoId'>> = [
  {
    type: 'document_front',
    title: 'Frente do Documento',
    caption: 'Documento — Frente',
    addLabel: 'ADICIONAR FOTO DA FRENTE',
  },
  {
    type: 'document_back',
    title: 'Verso do Documento',
    caption: 'Documento — Verso',
    addLabel: 'ADICIONAR FOTO DO VERSO',
  },
  {
    type: 'vehicle',
    title: 'Foto do Veículo (Placa)',
    caption: 'Foto do Veículo',
    addLabel: 'ADICIONAR FOTO DO VEÍCULO',
  },
];

/** The legacy column a slot falls back to, for the two that have one. */
const legacyUrlOf = (person: SlotPerson, type: DocumentSlotType): string | undefined =>
  type === 'document_front'
    ? person.document_front_url
    : type === 'document_back'
    ? person.document_back_url
    : undefined;

/**
 * The three document panels, each resolved to the photo it is currently showing.
 *
 * A photo is looked up by its `photo_type` tag first and only then in the legacy column, so a
 * person who has been re-photographed since the columns were retired shows the new photo. The
 * row id travels with it because the update flow deletes the row it replaces — resolving the
 * URL without it is what left people with two fronts and the older one winning the `find()`.
 */
export function documentSlots(person: SlotPerson | null | undefined): DocumentSlot[] {
  const photos = person?.identity_photos || [];

  return PANELS.map((panel) => {
    const record = photos.find((p) => p.photo_type === panel.type);
    const url = record?.photo_url || (person ? legacyUrlOf(person, panel.type) : undefined);
    return { ...panel, url: getAbsoluteImageUrl(url), photoId: record?.id };
  });
}
